import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAllLocations } from '../hooks/useAllLocations';
import { useRackPlant } from '../hooks/useRackPlant';
import {
  useAddMac,
  useCreateDevice,
  useDcimCatalog,
  useDcimDeviceDetail,
  useDcimDevices,
  useInstallModule,
  useRemoveModule,
  useRetireDevice,
  type ComponentRow,
  type DcimCatalog,
  type DcimDeviceRow,
  type DeviceInsert,
} from '../hooks/useDcim';
import {
  COMPONENT_KINDS,
  COMPONENT_KIND_LABEL,
  DEVICE_CONFIDENCES,
  DEVICE_STATUSES,
  compareComponents,
  countByKind,
  deviceTypeLabel,
  normalizeMac,
  rackHeightMismatch,
  summarizeCounts,
  type DeviceConfidence,
  type DeviceStatus,
} from '../lib/dcim';
import { ruLabel, type RackPlant } from '../lib/rackModel';
import { BTN, BTN_PRIMARY, ConfidenceChip, Feedback, INPUT, RoleDot, nullable, type FeedbackState } from '../components/dcim/ui';

/**
 * Devices at one site, each built from a device type. Creating a device is a
 * single insert; the database copies the type's interfaces, ports and bays.
 * A device links to its surveyed rack position rather than repeating it.
 */
export function DevicesPage() {
  const [params, setParams] = useSearchParams();
  const locationId = params.get('location') ?? '';
  const { data: locations } = useAllLocations();
  const { data: catalog } = useDcimCatalog();
  const { data: devices, isLoading, isError } = useDcimDevices(locationId || undefined);
  const { data: plant } = useRackPlant(locationId || undefined);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const sortedLocations = useMemo(() => [...(locations ?? [])].sort((a, b) => a.name.localeCompare(b.name)), [locations]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || !catalog) return devices ?? [];
    return (devices ?? []).filter((d) => {
      const type = catalog.deviceTypes.find((t) => t.id === d.device_type_id);
      return [d.name, d.serial, d.asset_tag, d.primary_ip, type?.model].filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [devices, catalog, search]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-text">Devices</h1>
          <p className="text-sm text-text-muted mt-1">
            {!locationId ? 'Choose a site.' : isLoading ? 'Loading…' : `${devices?.length ?? 0} device${devices?.length === 1 ? '' : 's'} modelled at this site`}
          </p>
        </div>
        <Link to="/device-types" className="text-sm text-primary hover:underline">Device types →</Link>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
        <label className="block sm:w-80">
          <span className="block text-xs text-text-muted mb-1">Site</span>
          <select
            value={locationId}
            onChange={(e) => { setParams(e.target.value ? { location: e.target.value } : {}); setOpen(null); setCreating(false); }}
            className={INPUT}
          >
            <option value="">Choose a site…</option>
            {sortedLocations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </label>
        {locationId && (
          <>
            <label className="block sm:w-72">
              <span className="sr-only">Search devices</span>
              <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, serial, IP, model" className={INPUT} />
            </label>
            <button type="button" onClick={() => setCreating((v) => !v)} aria-expanded={creating} className={BTN_PRIMARY}>
              {creating ? 'Close' : 'New device'}
            </button>
            <Link to={`/locations/${locationId}/racks`} className="text-sm text-primary hover:underline sm:ml-auto self-center">Rack Explorer</Link>
          </>
        )}
      </div>

      {isError && <p role="alert" className="rounded-lg border border-border bg-surface p-4 text-sm text-notification">Could not load devices. Refresh to try again.</p>}

      {creating && catalog && locationId && (
        <NewDeviceForm
          catalog={catalog}
          locationId={locationId}
          plant={plant}
          linkedUnits={new Set((devices ?? []).map((d) => d.rack_unit_id).filter((x): x is string => Boolean(x)))}
          onCreated={(id) => { setCreating(false); setOpen(id); }}
        />
      )}

      {locationId && !isLoading && (devices?.length ?? 0) === 0 && !creating && (
        <p className="rounded-lg border border-border bg-surface p-6 text-center text-sm text-text-muted">No devices modelled at this site yet.</p>
      )}

      {catalog && (
        <ul className="space-y-2">
          {filtered.map((d) => (
            <DeviceItem key={d.id} device={d} catalog={catalog} plant={plant} open={open === d.id} onToggle={() => setOpen(open === d.id ? null : d.id)} />
          ))}
        </ul>
      )}
    </div>
  );
}

function DeviceItem({ device: d, catalog, plant, open, onToggle }: { device: DcimDeviceRow; catalog: DcimCatalog; plant: RackPlant | undefined; open: boolean; onToggle: () => void }) {
  const type = catalog.deviceTypes.find((t) => t.id === d.device_type_id);
  const role = catalog.roles.find((r) => r.id === d.role_id);
  const unit = d.rack_unit_id ? plant?.unitById.get(d.rack_unit_id) : undefined;
  const rack = unit ? plant?.rackById.get(unit.rack_id) : undefined;
  const mismatch = type ? rackHeightMismatch(type.u_height, unit?.ru_height) : null;

  return (
    <li className="rounded-lg border border-border bg-surface">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full text-left p-3 min-h-[44px] flex flex-col md:flex-row md:items-center gap-1 md:gap-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
      >
        <span className="md:w-64 min-w-0">
          <span className="flex items-center gap-2">
            <span className="text-sm font-medium text-text truncate">{d.name}</span>
            <ConfidenceChip confidence={d.confidence} />
          </span>
          <span className="block text-xs text-text-muted truncate">
            {type ? deviceTypeLabel(catalog.manufacturerById.get(type.manufacturer_id)?.name, type.model) : 'Unknown type'}
          </span>
        </span>
        <span className="md:w-40 text-xs text-text">{role ? <RoleDot name={role.name} token={role.color_token} /> : '—'}</span>
        <span className="md:w-28 text-xs text-text-muted">{d.status}</span>
        <span className="flex-1 text-xs text-text-muted">
          {unit && rack ? `${rack.code} · ${ruLabel(unit)}` : 'No rack position'}
          {mismatch && <span className="text-warning"> · {mismatch}</span>}
        </span>
        <span className="text-xs font-mono text-text-faint">{d.primary_ip ?? ''}</span>
      </button>
      {open && <DeviceDetail device={d} catalog={catalog} />}
    </li>
  );
}

function DeviceDetail({ device, catalog }: { device: DcimDeviceRow; catalog: DcimCatalog }) {
  const { data, isLoading, isError } = useDcimDeviceDetail(device.id);
  const retire = useRetireDevice();
  const [message, setMessage] = useState<FeedbackState | null>(null);
  const sorted = useMemo(() => [...(data?.components ?? [])].sort(compareComponents), [data?.components]);
  const byId = useMemo(() => new Map(sorted.map((c) => [c.id, c])), [sorted]);

  const groups = COMPONENT_KINDS.map((kind) => ({ kind, rows: sorted.filter((c) => c.kind === kind) })).filter((g) => g.rows.length);

  const doRetire = async () => {
    if (!window.confirm(`Retire ${device.name}? It disappears from this list; its history stays in the audit log.`)) return;
    try {
      await retire.mutateAsync(device.id);
    } catch (e) {
      setMessage({ tone: 'error', text: `Could not retire: ${e instanceof Error ? e.message : String(e)}` });
    }
  };

  return (
    <div className="border-t border-border p-3 bg-surface-2 space-y-4">
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs">
        <Detail label="Serial" value={device.serial} mono />
        <Detail label="Asset tag" value={device.asset_tag} mono />
        <Detail label="Primary IP" value={device.primary_ip} mono />
        <Detail label="Platform" value={catalog.platforms.find((p) => p.id === device.platform_id)?.name ?? null} />
        <Detail label="Components" value={data ? summarizeCounts(countByKind(data.components)) : null} />
        <Detail label="Asset record" value={device.asset_id ? 'Linked' : null} />
        <Detail label="Auvik" value={device.observed_device_id ? 'Linked' : null} />
        <Detail label="Description" value={device.description} />
      </dl>

      {isLoading && <p className="text-sm text-text-muted">Loading components…</p>}
      {isError && <p role="alert" className="text-sm text-notification">Could not load components.</p>}

      {data && groups.length === 0 && <p className="text-sm text-text-muted">This device's type has no component templates.</p>}
      {data && groups.map((g) => (
        <section key={g.kind} aria-label={COMPONENT_KIND_LABEL[g.kind]}>
          <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-1">{COMPONENT_KIND_LABEL[g.kind]}s ({g.rows.length})</h4>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-xs">
              <tbody className="divide-y divide-divider bg-surface">
                {g.rows.map((c) => (
                  <ComponentLine
                    key={c.id}
                    component={c}
                    byId={byId}
                    device={device}
                    catalog={catalog}
                    macs={data.macsByComponent.get(c.id) ?? []}
                    module={data.modules.find((m) => m.module_bay_id === c.id)}
                    installedBy={c.module_id ? data.modules.find((m) => m.id === c.module_id) : undefined}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}

      <div className="flex items-center gap-3">
        <button type="button" onClick={() => void doRetire()} disabled={retire.isPending} className={`${BTN} text-error border border-border hover:bg-surface`}>
          Retire device
        </button>
        <Feedback message={message} />
      </div>
    </div>
  );
}

function ComponentLine({
  component: c,
  byId,
  device,
  catalog,
  macs,
  module,
  installedBy,
}: {
  component: ComponentRow;
  byId: Map<string, ComponentRow>;
  device: DcimDeviceRow;
  catalog: DcimCatalog;
  macs: { id: string; mac_address: string; is_primary: boolean }[];
  module: { id: string; module_type_id: string; serial: string | null } | undefined;
  installedBy: { module_type_id: string } | undefined;
}) {
  const detail: string[] = [];
  if (c.mgmt_only) detail.push('management');
  if (c.poe_mode) detail.push(`PoE ${c.poe_mode.toUpperCase()}`);
  if (c.maximum_draw_w) detail.push(`max ${c.maximum_draw_w} W`);
  if (c.feed_leg) detail.push(`leg ${c.feed_leg}`);
  if (c.power_port_id) detail.push(`fed by ${byId.get(c.power_port_id)?.name ?? '?'}`);
  if (c.positions) detail.push(`${c.positions} position${c.positions === 1 ? '' : 's'}`);
  if (c.rear_port_id) detail.push(`→ ${byId.get(c.rear_port_id)?.name ?? '?'} #${c.rear_port_position}`);
  if (installedBy) detail.push(`from ${catalog.moduleTypes.find((m) => m.id === installedBy.module_type_id)?.model ?? 'module'}`);

  return (
    <tr className={c.enabled ? undefined : 'opacity-60'}>
      <td className="px-2 py-1.5 font-mono text-text whitespace-nowrap align-top">{c.name}</td>
      <td className="px-2 py-1.5 font-mono text-text-muted align-top">{c.type ?? '—'}</td>
      <td className="px-2 py-1.5 text-text-muted align-top">{detail.join(' · ')}</td>
      <td className="px-2 py-1.5 align-top">
        {c.kind === 'interface' && <MacCell componentId={c.id} deviceId={device.id} macs={macs} />}
        {c.kind === 'module_bay' && <ModuleBayCell bay={c} deviceId={device.id} catalog={catalog} module={module} />}
      </td>
    </tr>
  );
}

function MacCell({ componentId, deviceId, macs }: { componentId: string; deviceId: string; macs: { id: string; mac_address: string; is_primary: boolean }[] }) {
  const add = useAddMac();
  const [value, setValue] = useState('');
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    const mac = normalizeMac(value);
    if (!mac) return setError('Not a MAC address.');
    try {
      await add.mutateAsync({ component_id: componentId, mac_address: mac, is_primary: macs.length === 0, deviceId });
      setValue('');
      setEditing(false);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {macs.map((m) => (
        <span key={m.id} className="font-mono text-text">{m.mac_address}{m.is_primary && macs.length > 1 ? ' (primary)' : ''}</span>
      ))}
      {editing ? (
        <form className="flex items-center gap-1" onSubmit={(e) => { e.preventDefault(); void save(); }}>
          <input value={value} onChange={(e) => { setValue(e.target.value); setError(null); }} className="w-36 rounded border border-border bg-surface px-1.5 py-0.5 font-mono text-xs text-text" placeholder="aa:bb:cc:dd:ee:ff" aria-label="MAC address" autoFocus />
          <button type="submit" className="text-primary hover:underline" disabled={add.isPending}>Save</button>
          <button type="button" className="text-text-muted hover:underline" onClick={() => { setEditing(false); setError(null); }}>Cancel</button>
        </form>
      ) : (
        <button type="button" className="text-primary hover:underline" onClick={() => setEditing(true)}>+ MAC</button>
      )}
      {error && <span role="alert" className="text-error">{error}</span>}
    </div>
  );
}

function ModuleBayCell({ bay, deviceId, catalog, module }: { bay: ComponentRow; deviceId: string; catalog: DcimCatalog; module: { id: string; module_type_id: string; serial: string | null } | undefined }) {
  const install = useInstallModule();
  const remove = useRemoveModule();
  const [typeId, setTypeId] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (module) {
    const mt = catalog.moduleTypes.find((m) => m.id === module.module_type_id);
    return (
      <span className="flex flex-wrap items-center gap-2">
        <span className="text-text">{mt ? deviceTypeLabel(catalog.manufacturerById.get(mt.manufacturer_id)?.name, mt.model) : 'Module'}</span>
        {module.serial && <span className="font-mono text-text-muted">{module.serial}</span>}
        <button
          type="button"
          className="text-error hover:underline"
          onClick={() => {
            if (window.confirm('Remove this module? Its interfaces and ports are removed with it.')) {
              remove.mutate({ id: module.id, deviceId }, { onError: (e) => setError(e instanceof Error ? e.message : String(e)) });
            }
          }}
        >
          Remove
        </button>
        {error && <span role="alert" className="text-error">{error}</span>}
      </span>
    );
  }
  if (catalog.moduleTypes.length === 0) return <span className="text-text-faint">Empty</span>;
  return (
    <form
      className="flex flex-wrap items-center gap-1"
      onSubmit={(e) => {
        e.preventDefault();
        if (!typeId) return;
        install.mutate(
          { device_id: deviceId, module_bay_id: bay.id, module_type_id: typeId, serial: null },
          { onError: (err) => setError(err instanceof Error ? err.message : String(err)), onSuccess: () => setTypeId('') },
        );
      }}
    >
      <select value={typeId} onChange={(e) => { setTypeId(e.target.value); setError(null); }} className="rounded border border-border bg-surface px-1.5 py-0.5 text-xs text-text" aria-label={`Module for ${bay.name}`}>
        <option value="">Empty — install…</option>
        {catalog.moduleTypes.map((m) => (
          <option key={m.id} value={m.id}>{deviceTypeLabel(catalog.manufacturerById.get(m.manufacturer_id)?.name, m.model)}</option>
        ))}
      </select>
      <button type="submit" className="text-primary hover:underline" disabled={!typeId || install.isPending}>Install</button>
      {error && <span role="alert" className="text-error">{error}</span>}
    </form>
  );
}

function Detail({ label, value, mono }: { label: string; value: string | null; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-text-faint">{label}</dt>
      <dd className={`text-text truncate ${mono ? 'font-mono' : ''}`}>{value ?? '—'}</dd>
    </div>
  );
}

function NewDeviceForm({
  catalog,
  locationId,
  plant,
  linkedUnits,
  onCreated,
}: {
  catalog: DcimCatalog;
  locationId: string;
  plant: RackPlant | undefined;
  linkedUnits: Set<string>;
  onCreated: (id: string) => void;
}) {
  const create = useCreateDevice();
  const [message, setMessage] = useState<FeedbackState | null>(null);
  const [form, setForm] = useState({
    name: '',
    device_type_id: '',
    role_id: catalog.roles[0]?.id ?? '',
    platform_id: '',
    status: 'active' as DeviceStatus,
    confidence: 'verified' as DeviceConfidence,
    serial: '',
    asset_tag: '',
    primary_ip: '',
    rack_unit_id: '',
    description: '',
  });

  const type = catalog.deviceTypes.find((t) => t.id === form.device_type_id);
  const templates = type ? catalog.templatesByOwner.get(type.id) ?? [] : [];
  const unit = form.rack_unit_id ? plant?.unitById.get(form.rack_unit_id) : undefined;
  const mismatch = type ? rackHeightMismatch(type.u_height, unit?.ru_height) : null;

  // Surveyed positions not already claimed by a device, vacancies excluded.
  const freeUnits = useMemo(() => {
    if (!plant) return [];
    return plant.units
      .filter((u) => u.kind !== 'vacant' && !linkedUnits.has(u.id))
      .map((u) => ({ u, rack: plant.rackById.get(u.rack_id) }))
      .sort((a, b) => (a.rack?.code ?? '').localeCompare(b.rack?.code ?? '') || b.u.ru_start - a.u.ru_start);
  }, [plant, linkedUnits]);

  const typeOptions = useMemo(
    () =>
      catalog.deviceTypes
        .filter((t) => t.subdevice_role !== 'child')
        .map((t) => ({ id: t.id, label: deviceTypeLabel(catalog.manufacturerById.get(t.manufacturer_id)?.name, t.model) }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true })),
    [catalog],
  );

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = e.target.value;
    setForm((f) => {
      const next = { ...f, [key]: value };
      // A type brings its default platform, unless one was already chosen.
      if (key === 'device_type_id' && !f.platform_id) {
        next.platform_id = catalog.deviceTypes.find((t) => t.id === value)?.default_platform_id ?? '';
      }
      return next;
    });
    setMessage(null);
  };

  const submit = async () => {
    if (!form.name.trim()) return setMessage({ tone: 'error', text: 'Name is required.' });
    if (!form.device_type_id) return setMessage({ tone: 'error', text: 'Choose a device type.' });
    if (!form.role_id) return setMessage({ tone: 'error', text: 'Choose a role (add one under Device Types → Manufacturers, platforms & roles).' });
    const ip = nullable(form.primary_ip);
    if (ip && !/^[0-9a-f:.]+(\/\d{1,3})?$/i.test(ip)) return setMessage({ tone: 'error', text: 'Primary IP must be an IPv4 or IPv6 address.' });
    const row: DeviceInsert = {
      location_id: locationId,
      name: form.name.trim(),
      device_type_id: form.device_type_id,
      role_id: form.role_id,
      platform_id: nullable(form.platform_id),
      status: form.status,
      confidence: form.confidence,
      serial: nullable(form.serial),
      asset_tag: nullable(form.asset_tag),
      primary_ip: ip,
      rack_unit_id: nullable(form.rack_unit_id),
      description: nullable(form.description),
    };
    try {
      onCreated(await create.mutateAsync(row));
    } catch (e) {
      setMessage({ tone: 'error', text: `Could not create: ${e instanceof Error ? e.message : String(e)}` });
    }
  };

  return (
    <form className="rounded-lg border border-border bg-surface p-3 space-y-3" onSubmit={(e) => { e.preventDefault(); void submit(); }} aria-label="New device">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Labelled label="Name"><input value={form.name} onChange={set('name')} className={INPUT} placeholder="SAQ-MDF-SW01" required /></Labelled>
        <Labelled label="Device type">
          <select value={form.device_type_id} onChange={set('device_type_id')} className={INPUT} required>
            <option value="">Choose…</option>
            {typeOptions.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </Labelled>
        <Labelled label="Role">
          <select value={form.role_id} onChange={set('role_id')} className={INPUT} required>
            <option value="">Choose…</option>
            {catalog.roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </Labelled>
        <Labelled label="Platform">
          <select value={form.platform_id} onChange={set('platform_id')} className={INPUT}>
            <option value="">—</option>
            {catalog.platforms.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Labelled>
        <Labelled label="Status">
          <select value={form.status} onChange={set('status')} className={INPUT}>
            {DEVICE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Labelled>
        <Labelled label="Confidence" hint="How the device's identity was established.">
          <select value={form.confidence} onChange={set('confidence')} className={INPUT}>
            {DEVICE_CONFIDENCES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Labelled>
        <Labelled label="Serial"><input value={form.serial} onChange={set('serial')} className={INPUT} /></Labelled>
        <Labelled label="Asset tag"><input value={form.asset_tag} onChange={set('asset_tag')} className={INPUT} /></Labelled>
        <Labelled label="Primary IP"><input value={form.primary_ip} onChange={set('primary_ip')} className={INPUT} placeholder="10.0.3.131" /></Labelled>
        <Labelled label="Rack position" hint={plant && plant.units.length === 0 ? 'No racks surveyed at this site yet.' : 'A surveyed position from the Rack Explorer.'}>
          <select value={form.rack_unit_id} onChange={set('rack_unit_id')} className={INPUT}>
            <option value="">None</option>
            {freeUnits.map(({ u, rack }) => (
              <option key={u.id} value={u.id}>{rack?.code ?? '?'} · {ruLabel(u)} · {u.label}</option>
            ))}
          </select>
        </Labelled>
        <Labelled label="Description"><input value={form.description} onChange={set('description')} className={INPUT} /></Labelled>
      </div>
      {type && (
        <p className="text-xs text-text-muted">
          Will be built with {summarizeCounts(countByKind(templates)).toLowerCase()}.
          {mismatch && <span className="text-warning"> {mismatch}</span>}
        </p>
      )}
      <button type="submit" disabled={create.isPending} className={BTN_PRIMARY}>{create.isPending ? 'Creating…' : 'Create device'}</button>
      <Feedback message={message} />
    </form>
  );
}

function Labelled({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-text-muted mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-text-faint mt-0.5">{hint}</span>}
    </label>
  );
}
